'use client';

import Link from 'next/link';
import React from 'react';
import { FaStar } from 'react-icons/fa';
import { ApiRoutes, Routes } from '@/settings/routes';
import { App_text } from '@/settings/AppText';
import { FaRegClock } from 'react-icons/fa6';
import { SiTripadvisor } from 'react-icons/si';

export default function CardPackageHome({ data = {} }) {
    const getThumb = () => {
        const thumb = data?.thumbnail;
        if (typeof thumb === 'string' && thumb) return ApiRoutes.ASSETS_ENDPOINT + thumb;
        return '/media/tour-06.jpg';
    };

    const href = data?.category_slug ? `${Routes.FRONT_packages_category}${data.category_slug}` : Routes.FRONT_city_breaks;

    const truncateText = (text, maxLength = 80) => {
        if (!text) return '';
        const plainText = text.replace(/<[^>]*>/g, '');
        if (plainText.length <= maxLength) return plainText;
        const lastSpace = plainText.substring(0, maxLength).lastIndexOf(' ');
        const truncLength = lastSpace === -1 ? maxLength : lastSpace;
        return plainText.substring(0, truncLength) + '...';
    };

    const calculateDiscount = () => {
        if (data?.has_promo && data?.price && data?.price_promo) {
            const discount = ((data?.price - data?.price_promo) / data?.price) * 100;
            return discount.toFixed();
        }
        return 0;
    };

    const discount = calculateDiscount();
    const isPrivate = data?.type === 'private';

    return (
        <Link href={href} className="block overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm transition-all duration-300 group hover:shadow-lg hover:-translate-y-1">
            {/* Image Container */}
            <div className="overflow-hidden relative h-56">
                <img src={getThumb()} alt={data?.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-110" />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t to-transparent from-black/70 via-black/20" />

                {/* Duration Badge */}
                {data?.duration && (
                    <div className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-lg shadow-md">
                        <FaRegClock className="w-3.5 h-3.5 text-custom-orange-600" />
                        <span className="text-xs font-semibold text-gray-900">{data.duration}</span>
                    </div>
                )}

                {/* Discount Badge - Only for non-private tours with discount */}
                {!isPrivate && discount > 0 && (
                    <div className="absolute top-3 right-3">
                        <div className="flex flex-col justify-center items-center w-14 h-14 rounded-full shadow-lg bg-custom-orange-600">
                            <span className="text-lg font-bold leading-none text-white">{discount}%</span>
                            <span className="text-[10px] font-medium text-white">OFF</span>
                        </div>
                    </div>
                )}

                {/* Category Badge - Show when no discount or private tour */}
                {(isPrivate || (!isPrivate && !discount)) && data?.category_title && (
                    <div className="absolute top-3 right-3 px-3 py-1.5 bg-custom-orange-600 rounded-lg shadow-md">
                        <span className="text-xs font-semibold tracking-wide text-white uppercase">{data.category_title}</span>
                    </div>
                )}
            </div>

            {/* Content Container */}
            <div className="p-5">
                {/* Title */}
                <h3 className="mb-3 text-lg font-bold transition-colors text-custom-color-headline line-clamp-2 group-hover:text-custom-orange-600">{data?.title}</h3>

                {/* Description */}
                {data?.description && <p className="mb-4 text-sm text-gray-600 line-clamp-2">{truncateText(data.description)}</p>}

                {/* Divider */}
                <div className="pt-4 border-t border-gray-100">
                    {/* Price Section */}
                    <div className="flex justify-between items-end mb-4">
                        <div>
                            <div className="mb-1 text-xs tracking-wide text-gray-500 uppercase">{isPrivate ? App_text.card_tour?.price || 'Price' : App_text.card_tour?.price || 'Starting from'}</div>

                            {isPrivate ? (
                                // Private Tour - Show "Upon Request"
                                <div className="text-lg font-semibold text-custom-orange-600">{App_text.card_tour?.text_price || 'Price on request'}</div>
                            ) : (
                                // Non-Private Tour - Show Price
                                <>
                                    {data?.price !== null && data?.price !== 0 && data?.price !== '0.00' ? (
                                        data?.has_promo ? (
                                            <div className="flex gap-2 items-baseline">
                                                <span className="text-2xl font-bold text-custom-orange-600">
                                                    {data?.price_promo}
                                                    {App_text.generale?.currancy || '€'}
                                                </span>
                                                <span className="text-sm text-gray-400 line-through">
                                                    {data?.price}
                                                    {App_text.generale?.currancy || '€'}
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="flex items-baseline">
                                                <span className="text-2xl font-bold text-custom-orange-600">
                                                    {data?.price}
                                                    {App_text.generale?.currancy || '€'}
                                                </span>
                                                <span className="ml-1 text-xs text-gray-500">/pers</span>
                                            </div>
                                        )
                                    ) : (
                                        <div className="text-lg font-semibold text-custom-orange-600">Price on request</div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Rating */}
                        <div className="flex gap-2 items-center">
                            <div className="flex gap-1 items-center">
                                <FaStar className="w-4 h-4 text-yellow-400" />
                                <span className="text-sm font-bold text-gray-900">5.0</span>
                            </div>
                            <SiTripadvisor className="w-5 h-5 text-green-500" />
                        </div>
                    </div>

                    {/* View Details */}
                    <div className="flex justify-center items-center pt-3 border-t border-gray-100">
                        <span className="inline-flex gap-1 items-center text-sm font-semibold transition-all text-custom-orange-600 group-hover:gap-2">
                            <span>View Details</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </span>
                    </div>
                </div>
            </div>
        </Link>
    );
}
